import {
  OpsTatAppliedFilters,
  OpsTatPath,
} from "../models/Dashboard/OpsTat/OpsTatFilters";
import {
  CommanderActionContext,
  trackCommanderAction,
} from "./commanderTracking";
import { OpsTatActionId } from "./catalog/opsTatActions";

/** GA4 custom parameter string values are truncated around 100 chars. */
const GA_PARAM_STRING_MAX = 100;

const PATH_LABELS: Record<OpsTatPath, string> = {
  all: "All",
  custom: "Custom",
  stock: "Stock",
};

/** Human titles matching Ops TAT cards / tiles (DebugView + action_name). */
export const OPS_TAT_CARD_LABELS: Record<string, string> = {
  order_to_pr: "Order → PR raised",
  pr_to_po: "PR → PO issued",
  po_to_sent: "PO → sent to vendor",
  po_to_raw_in: "PO created → Raw inward (full)",
  raw_in_to_raw_qc: "Raw inward → Raw QC cleared",
  raw_qc_to_print_out: "Raw QC → Print outward (full)",
  print_out_to_print_in: "Print outward → Print inward (full)",
  print_in_to_print_qc: "Print inward → Print QC cleared",
  cycle_total: "Cycle total",
  sla_adherence: "On-SLA orders",
  top_drag: "Top drag",
  custom_mix: "Customised mix",
  dispatch_deviation: "Dispatch deviation",
  fill_rate: "Promise fill rate",
  raw_inward_dev: "Raw inward · mean deviation",
  print_inward_dev: "Print inward · mean deviation",
  raw_qc_dev: "Raw QC · mean deviation",
  print_qc_dev: "Print QC · mean deviation",
  bias: "Vendor bias",
  open_overdue: "Open overdue",
  suggested_use: "Suggested use",
  vendor_dev: "Vendor deviation",
};

const SECTION_LABELS: Record<string, string> = {
  stage_turnaround: "Stage turnaround",
  days_ledger: "Days ledger",
  dispatch: "Dispatch",
  inward_deviation: "Inward deviation",
};

/** Readable labels for quick date preset chips. */
const PRESET_LABELS: Record<string, string> = {
  "7d": "Last 7 days",
  "30d": "Last 30 days",
  "90d": "Last 90 days",
  "6m": "Last 6 months",
  fy: "This financial year",
};

/**
 * Caps a description to GA's practical string limit.
 *
 * @param text - Full description.
 * @returns Truncated description.
 */
function clipDescription(text: string): string {
  return text.slice(0, GA_PARAM_STRING_MAX);
}

/**
 * Human label for a clickable Ops TAT card / metric.
 *
 * @param metricId - Stable metric id from the card.
 * @param override - Optional UI title when it differs from the map.
 * @returns Readable card title.
 */
export function opsTatCardLabel(
  metricId?: string | null,
  override?: string | null,
): string {
  if (override && override.trim()) return override.trim();
  if (!metricId) return "card";
  return OPS_TAT_CARD_LABELS[metricId] || metricId;
}

/**
 * Human label for which dashboard section the card lives in.
 *
 * @param section - Section key (stage_turnaround, days_ledger, …).
 * @returns Readable section label.
 */
export function opsTatCardSectionLabel(section?: string | null): string {
  if (!section) return "";
  return SECTION_LABELS[section] || section;
}

/**
 * Human label for All / Custom / Stock path chips.
 *
 * @param path - Ops TAT path id.
 * @returns Readable path label.
 */
export function opsTatPathLabel(path?: string | null): string {
  if (!path) return "All";
  return PATH_LABELS[path as OpsTatPath] || String(path);
}

/**
 * Virtual screen name — each path is a distinct screen in GA.
 *
 * @param path - Ops TAT path id.
 * @returns Screen name for GA params.
 */
export function opsTatScreenName(path?: string | null): string {
  return `Operations TAT › ${opsTatPathLabel(path)}`;
}

/**
 * Slim shared context for every Operations TAT GA event.
 *
 * @param filters - Applied Ops TAT filters.
 * @returns Context params merged into the GA event.
 */
export function opsTatFilterContext(
  filters?: OpsTatAppliedFilters | null,
): CommanderActionContext {
  if (!filters) {
    return { screen_name: "Operations TAT" };
  }

  const projectStatus =
    filters.project_status === null
      ? "both"
      : filters.project_status
        ? "archived"
        : "active";

  return {
    screen_name: opsTatScreenName(filters.path),
    path: filters.path,
    path_label: opsTatPathLabel(filters.path),
    start_date: filters.start_date.format("YYYY-MM-DD"),
    end_date: filters.end_date.format("YYYY-MM-DD"),
    order_id_count: filters.order_id?.length ?? 0,
    order_type_count: filters.order_type?.length ?? 0,
    project_poc_count: filters.project_poc_id?.length ?? 0,
    sourcing_poc_count: filters.sourcing_poc_id?.length ?? 0,
    vendor_id_count: filters.vendor_id?.length ?? 0,
    project_status: projectStatus,
    excluded_orders: filters.excluded_orders_from_sourcing,
    include_running: filters.include_running,
  };
}

type LabelExtra = {
  path?: string;
  previousPath?: string;
  preset?: string;
  expanded?: boolean;
  metricId?: string;
  cardTitle?: string;
  cardSection?: string;
  drawerSource?: string;
  vendorId?: number | string | null;
  recordScope?: string;
  sortColumn?: string;
  sortDir?: string;
  page?: number;
  pageSize?: number;
  previousPageSize?: number;
  section?: string;
};

/**
 * Builds `ops_tat_db_{metric}_{verb}` so DebugView timeline shows which card.
 *
 * @param metricId - Card metric id.
 * @param verb - open | close | scope | sort | page | rows | export.
 * @returns Event name ≤40 chars.
 */
function opsTatCardEventName(
  metricId: string | undefined,
  verb: string,
): string {
  const id = String(metricId || "card")
    .replace(/[^a-z0-9_]/gi, "_")
    .slice(0, 22);
  return `ops_tat_db_${id}_${verb}`.slice(0, 40);
}

/**
 * Builds dynamic action_name + action_description for Operations TAT GA.
 *
 * @param actionId - Stable ops tat action id.
 * @param filters - Filters for brief context.
 * @param extra - Optional action-specific label inputs.
 * @returns Override labels for trackCommanderAction.
 */
export function opsTatActionLabels(
  actionId: string,
  filters?: OpsTatAppliedFilters | null,
  extra?: LabelExtra,
): Pick<CommanderActionContext, "action_name" | "action_description"> {
  const pathLabel = opsTatPathLabel(extra?.path || filters?.path);
  const start = filters?.start_date?.format("YYYY-MM-DD") || "";
  const end = filters?.end_date?.format("YYYY-MM-DD") || "";
  const brief = start && end ? `${start}→${end}` : "current filters";
  const card = opsTatCardLabel(extra?.metricId, extra?.cardTitle);
  const section = opsTatCardSectionLabel(extra?.cardSection);
  const sectionBit = section ? ` · ${section}` : "";
  const vendorBit =
    extra?.vendorId != null ? ` · vendor ${extra.vendorId}` : "";

  switch (actionId) {
    case "dashboard_view":
      return {
        action_name: `Opened Operations TAT › ${pathLabel}`,
        action_description: clipDescription(
          `Loaded Operations TAT on ${pathLabel} path. ${brief}`,
        ),
      };
    case "filter_apply":
      return {
        action_name: `Applied filters on ${pathLabel}`,
        action_description: clipDescription(
          `Refreshed Operations TAT › ${pathLabel}. ${brief}`,
        ),
      };
    case "dashboard_refresh":
      return {
        action_name: `Refreshed Operations TAT › ${pathLabel}`,
        action_description: clipDescription(
          `Reloaded Operations TAT on ${pathLabel} with applied filters. ${brief}`,
        ),
      };
    case "drilldown_refresh":
      return {
        action_name: `Refreshed card: ${card}`,
        action_description: clipDescription(
          `Reloaded "${card}"${sectionBit} on ${pathLabel}${vendorBit}. ${brief}`,
        ),
      };
    case "filter_clear":
      return {
        action_name: `Cleared filters on ${pathLabel}`,
        action_description: clipDescription(
          `Reset Operations TAT filters on ${pathLabel} (path kept)`,
        ),
      };
    case "date_preset_select": {
      const presetId = extra?.preset || "date";
      const presetLabel =
        PRESET_LABELS[presetId] || String(presetId).toUpperCase();
      return {
        action_name: `Selected ${presetLabel}`,
        action_description: clipDescription(
          `Applied date preset ${presetLabel} on Operations TAT › ${pathLabel}`,
        ),
      };
    }
    case "date_range_change":
      return {
        action_name: `Picked custom dates ${brief}`,
        action_description: clipDescription(
          `Changed custom date range to ${brief} on ${pathLabel} (draft)`,
        ),
      };
    case "search_panel_toggle":
      return {
        action_name: `${extra?.expanded ? "Expanded" : "Collapsed"} search panel`,
        action_description: clipDescription(
          `${extra?.expanded ? "Expanded" : "Collapsed"} Operations TAT Search panel`,
        ),
      };
    case "filter_order_id_change":
      return {
        action_name: "Changed order ID (draft)",
        action_description: clipDescription(
          `Updated order ID filter on ${pathLabel} (Apply to refresh)`,
        ),
      };
    case "filter_order_type_change":
      return {
        action_name: "Changed order type (draft)",
        action_description: clipDescription(
          `Updated order type filter on ${pathLabel} (Apply to refresh)`,
        ),
      };
    case "filter_project_poc_change":
      return {
        action_name: "Changed project POC (draft)",
        action_description: clipDescription(
          `Updated project POC filter on ${pathLabel} (Apply to refresh)`,
        ),
      };
    case "filter_sourcing_poc_change":
      return {
        action_name: "Changed sourcing POC (draft)",
        action_description: clipDescription(
          `Updated sourcing POC filter on ${pathLabel} (Apply to refresh)`,
        ),
      };
    case "filter_vendor_change":
      return {
        action_name: "Changed vendor (draft)",
        action_description: clipDescription(
          `Updated vendor filter on ${pathLabel} (Apply to refresh)`,
        ),
      };
    case "filter_project_status_change":
      return {
        action_name: "Changed project status (draft)",
        action_description: clipDescription(
          `Updated project status on ${pathLabel} (Apply to refresh)`,
        ),
      };
    case "filter_excluded_orders_change":
      return {
        action_name: "Changed excluded orders (draft)",
        action_description: clipDescription(
          `Toggled excluded-from-sourcing on ${pathLabel} (Apply to refresh)`,
        ),
      };
    case "filter_include_running_change":
      return {
        action_name: "Changed include running (draft)",
        action_description: clipDescription(
          `Toggled include running on ${pathLabel} (Apply to refresh)`,
        ),
      };
    case "path_change": {
      const from = extra?.previousPath
        ? opsTatPathLabel(extra.previousPath)
        : "previous";
      return {
        action_name: `Switched to ${pathLabel} path`,
        action_description: clipDescription(
          `Left ${from}, opened ${pathLabel} path. ${brief}`,
        ),
      };
    }
    case "drilldown_open":
      return {
        action_name: `Opened card: ${card}`,
        action_description: clipDescription(
          `Opened "${card}"${sectionBit} on ${pathLabel}${vendorBit}`,
        ),
      };
    case "drilldown_close":
      return {
        action_name: `Closed card: ${card}`,
        action_description: clipDescription(
          `Closed "${card}"${sectionBit} on ${pathLabel}`,
        ),
      };
    case "drilldown_scope_change":
      return {
        action_name: `${card} scope → ${extra?.recordScope}`,
        action_description: clipDescription(
          `On "${card}": switched scope to ${extra?.recordScope}${sectionBit}`,
        ),
      };
    case "drilldown_sort":
      return {
        action_name: `Sorted ${card} by ${extra?.sortColumn} ${extra?.sortDir}`,
        action_description: clipDescription(
          `On "${card}": sorted by ${extra?.sortColumn} ${extra?.sortDir}${sectionBit}`,
        ),
      };
    case "drilldown_page_change":
      return {
        action_name: `${card} page → ${extra?.page}`,
        action_description: clipDescription(
          `On "${card}": went to page ${extra?.page}${sectionBit}`,
        ),
      };
    case "drilldown_page_size_change":
      return {
        action_name: `${card} rows → ${extra?.pageSize}`,
        action_description: clipDescription(
          `On "${card}": rows per page → ${extra?.pageSize}${sectionBit}`,
        ),
      };
    case "export_csv":
      return {
        action_name: `Exported CSV: ${card}`,
        action_description: clipDescription(
          `Exported CSV for "${card}"${sectionBit} on ${pathLabel}. ${brief}`,
        ),
      };
    case "formula_toggle":
      return {
        action_name: `${extra?.expanded ? "Expanded" : "Collapsed"} formula · ${card}`,
        action_description: clipDescription(
          `${extra?.expanded ? "Expanded" : "Collapsed"} How this is calculated on "${card}"`,
        ),
      };
    case "how_to_read_toggle":
      return {
        action_name: `${extra?.expanded ? "Expanded" : "Collapsed"} how to read`,
        action_description: clipDescription(
          `${extra?.expanded ? "Expanded" : "Collapsed"} How to read (${extra?.section || "section"})`,
        ),
      };
    case "vendor_page_change":
      return {
        action_name: `Vendor table page → ${extra?.page}`,
        action_description: clipDescription(
          `Changed inward vendors table to page ${extra?.page}`,
        ),
      };
    default:
      return {
        action_name: undefined,
        action_description: clipDescription(
          `Operations TAT action "${actionId}" on ${pathLabel}. ${brief}`,
        ),
      };
  }
}

type OpsTatTrackExtra = LabelExtra & {
  context?: CommanderActionContext;
};

/**
 * Preferred helper for Operations TAT GA.
 * Fires exactly one event per call; never throws into UI/store flows.
 *
 * @param actionId - Catalog action id.
 * @param filters - Applied filters for slim context.
 * @param extra - Optional label inputs + raw context fields.
 */
export function trackOpsTatAction(
  actionId: OpsTatActionId,
  filters?: OpsTatAppliedFilters | null,
  extra?: OpsTatTrackExtra,
): void {
  try {
    const { context: extraContext, ...labelExtra } = extra || {};
    const path = labelExtra.path || filters?.path || "all";
    const metricId = labelExtra.metricId;
    const cardTitle = opsTatCardLabel(metricId, labelExtra.cardTitle);
    const cardSection = labelExtra.cardSection;

    let eventName = `ops_tat_db_${actionId}`;
    if (actionId === "dashboard_view") {
      eventName = "ops_tat_db_view";
    } else if (actionId === "filter_apply") {
      eventName = "ops_tat_db_filters_apply";
    } else if (actionId === "filter_clear") {
      eventName = "ops_tat_db_filters_clear";
    } else if (actionId === "dashboard_refresh") {
      eventName = "ops_tat_db_refresh";
    } else if (actionId === "drilldown_refresh") {
      eventName = opsTatCardEventName(metricId, "refresh");
    } else if (actionId === "date_preset_select") {
      const presetKey = String(labelExtra.preset || "date")
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "");
      eventName = `ops_tat_db_preset_${presetKey || "date"}`.slice(0, 40);
    } else if (actionId === "date_range_change") {
      eventName = "ops_tat_db_custom_dates";
    } else if (actionId === "search_panel_toggle") {
      eventName = "ops_tat_db_filters_panel";
    } else if (actionId === "path_change") {
      eventName = `ops_tat_db_path_${path}`.slice(0, 40);
    } else if (actionId === "drilldown_open") {
      eventName = opsTatCardEventName(metricId, "open");
    } else if (actionId === "drilldown_close") {
      eventName = opsTatCardEventName(metricId, "close");
    } else if (actionId === "drilldown_scope_change") {
      eventName = opsTatCardEventName(metricId, "scope");
    } else if (actionId === "drilldown_sort") {
      eventName = opsTatCardEventName(metricId, "sort");
    } else if (actionId === "drilldown_page_change") {
      eventName = opsTatCardEventName(metricId, "page");
    } else if (actionId === "drilldown_page_size_change") {
      eventName = opsTatCardEventName(metricId, "rows");
    } else if (actionId === "export_csv") {
      eventName = opsTatCardEventName(metricId, "export");
    } else if (actionId === "formula_toggle") {
      eventName = opsTatCardEventName(metricId, "formula");
    } else if (actionId === "how_to_read_toggle") {
      eventName = "ops_tat_db_how_to_read";
    } else if (actionId === "vendor_page_change") {
      eventName = "ops_tat_db_vendor_page";
    } else if (actionId.startsWith("filter_")) {
      const short = actionId.replace("filter_", "").replace("_change", "");
      eventName = `ops_tat_db_f_${short}`.slice(0, 40);
    }

    trackCommanderAction({
      moduleId: "ops_tat",
      actionId,
      eventName,
      context: {
        ...opsTatFilterContext(filters),
        ...opsTatActionLabels(actionId, filters, {
          ...labelExtra,
          path,
        }),
        ...(metricId
          ? {
              metric_id: metricId,
              card_title: cardTitle,
              ...(cardSection ? { card_section: cardSection } : {}),
            }
          : {}),
        ...(extraContext || {}),
      },
    });
  } catch {
    // never block UI
  }
}
